import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-checkbox-type-three',
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
    templateUrl: './checkbox-type-three.component.html',
    styleUrl: './checkbox-type-three.component.css'
})
export class CheckboxTypeThreeComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() nodeId: any;
  fieldsData: any;
  selectedIds: string[] = [];
  showValidationError: boolean = false;
  endpoint = environment.api_endpoint;
  control!: FormControl;
  isLoading: boolean = true;
  skeletonArray = Array.from({ length: 6 });
  @Output() handleTaskMappingDependency = new EventEmitter<{
    fieldName: string;
    tags: any[];
    selectedQuestion: any;
  }>();
  constructor(
    private formService: FormsServiceService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.getDetails();
    this.control = this.formGroup.get(this.fields.fieldName) as FormControl;
  }

  getDetails() {
    this.getData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['formGroup'] || changes['fields']) &&
      this.formGroup &&
      this.fields
    ) {
      this.restoreSelectionFromControl();
    }
  }

  isSelected(item: any) {
    if (item.cloudId) {
      return this.selectedIds.includes(item.cloudId?.toString());
    } else {
      return this.selectedIds.includes(item._id?.toString());
    }
  }

  /** Function to get the data by api or static */
  getData() {
    this.isLoading = true;
    if (this.fields.options.isStatic === true) {
      this.fieldsData = this.fields.options.staticOptions;
      // this.initializeSelection();
      this.isLoading = false;
    } else {
      const formId = this.fields.options.source.formID;
      const api_endpoint = this.endpoint;
      const method = this.fields.options.source.method;
      let url = this.fields.options.source.apiUrl;
      const replacements = {
        formID: formId,
        api_endpoint: api_endpoint,
      };
      url = this.formService.replaceTemplateTokens(url, replacements);
      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      this.formService.loadData(method, url, headers).subscribe({
        next: (res: any) => {
          this.fieldsData = res.data.records;
          this.restoreSelectionFromControl();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.fieldsData = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
    }
  }

  restoreSelectionFromControl() {
    if (!this.control || !this.fieldsData) return;

    let rawValue = this.control?.value ?? this.fields?.value ?? [];

    if (!Array.isArray(rawValue)) {
      rawValue = [];
    }

    const normalized = rawValue.map((v: any) => {
      if (typeof v === 'object' && v !== null) {
        return (v.cloudId || v._id || '').toString();
      }
      return v?.toString();
    });

    // ✅ store IDs only (strings)
    this.selectedIds = normalized.filter((id: string) =>
      this.fieldsData.some(
        (item: any) =>
          item.cloudId?.toString() === id || item._id?.toString() === id,
      ),
    );

    // ✅ find selected options
    const selectedOptions = this.fieldsData.filter(
      (opt: any) =>
        this.selectedIds.includes(opt._id?.toString()) ||
        this.selectedIds.includes(opt.cloudId?.toString()),
    );

    // ✅ restore IDs (FIXED)
    this.control?.setValue(this.selectedIds, { emitEvent: false });

    // ✅ resolve fieldName
    const fieldName = this.resolveFieldName(this.fields);

    // ✅ restore tags
    const tagNames = selectedOptions.map((opt: any) => opt.shortName || opt.name || '');

    const tagsControl = this.formGroup.get(fieldName + 'Tags');
    tagsControl?.setValue(tagNames, { emitEvent: false });

    let selectedQuestion = this.fields.displayName || '';
    if (selectedQuestion) {
      selectedQuestion = selectedQuestion.replace(/^\d+\.\s*/, '').replace(/[^a-zA-Z0-9)\]\s]+$/, '').trim();
    }

    // ✅ FIXED EMITTER
    this.handleTaskMappingDependency.emit({
      fieldName,
      tags: tagNames,
      selectedQuestion: selectedQuestion,
    });
  }

  private resolveFieldName(field: any): string {
    if (!field) return '';

    // ✅ Case 1: direct string
    if (typeof field.fieldName === 'string') {
      return field.fieldName;
    }

    // ✅ Case 2: object { label, value }
    if (field.fieldName?.label) {
      return field.fieldName.label;
    }

    // ✅ Case 3: value[] format
    if (Array.isArray(field)) {
      return this.getValueByKey(field, 'fieldName') || '';
    }

    // ✅ Case 4: field.value is array (very common in your setup)
    if (Array.isArray(field.value)) {
      return this.getValueByKey(field.value, 'fieldName') || '';
    }

    return '';
  }

  private getValueByKey(arr: any[], key: string): any {
    return (arr || []).find((item: any) => item.Key === key)?.Value;
  }

  onToggle(event: any, id: string) {
    const idStr = id?.toString();
    const checked = event.target.checked;
    const fieldType = this.fields.fieldType;
    const fieldName = this.resolveFieldName(this.fields);

    // -----------------------------
    // ✅ Case 1: Multi-Select (Checkbox)
    // -----------------------------
    if (fieldType === 'multiSelect') {
      let curr: string[] = Array.isArray(this.control?.value)
        ? this.control.value.map((v: any) => v.toString())
        : [];

      let next: string[];
      if (checked) {
        next = curr.includes(idStr) ? curr : [...curr, idStr];
      } else {
        next = curr.filter((v: any) => v !== idStr);
      }

      this.control.setValue(next, { emitEvent: true });
      this.selectedIds = [...next];

      const tagsControl = this.formGroup.get(fieldName + 'Tags');
      if (tagsControl) {
        const selectedTags = this.fieldsData
          .filter(
            (opt: any) =>
              next.includes(opt._id?.toString()) ||
              next.includes(opt.cloudId?.toString()),
          )
          .map((opt: any) => opt.shortName || opt.name || '');
        tagsControl.setValue(selectedTags, { emitEvent: true });
      }

      this.showValidationError = this.fields.isRequired && next.length === 0;
    }

    // -----------------------------
    // ✅ Case 2: Radio (Single Select)
    // -----------------------------
    else if (fieldType === 'radio') {
      this.control.setValue(idStr, { emitEvent: true });
      this.selectedIds = [idStr];

      const tagsControl = this.formGroup.get(fieldName + 'Tags');
      if (tagsControl) {
        const selectedOption = this.fieldsData.find(
          (opt: any) => opt.cloudId?.toString() === idStr,
        );
        const selectedTags = selectedOption ? [selectedOption.name] : [];
        tagsControl.setValue(selectedTags, { emitEvent: true });
      }

      this.showValidationError = false;
    }
    const tagsControl = this.formGroup.get(fieldName + 'Tags');
    const tags = tagsControl ? tagsControl.value : [];
    let selectedQuestion = this.fields.displayName || '';
    if (selectedQuestion) {
      selectedQuestion = selectedQuestion.replace(/^\d+\.\s*/, '').replace(/[^a-zA-Z0-9)\]\s]+$/, '').trim();
    }

    this.handleTaskMappingDependency.emit({
      fieldName: fieldName,
      tags: tags,
      selectedQuestion: selectedQuestion
    });
  }

  isAllSelected(): boolean {
    if (!this.fieldsData || this.fieldsData.length === 0) return false;
    return this.fieldsData.every((item: any) => {
      const id = (item.cloudId || item._id)?.toString();
      return this.selectedIds.includes(id);
    });
  }

  toggleSelectAll(event: any) {
    const checked = event.target.checked;
    const fieldName = this.resolveFieldName(this.fields);
    if (checked) {
      const next = this.fieldsData.map((item: any) => (item.cloudId || item._id).toString());
      this.control.setValue(next, { emitEvent: true });
      this.selectedIds = [...next];

      const tagsControl = this.formGroup.get(fieldName + 'Tags');
      if (tagsControl) {
        const selectedTags = this.fieldsData.map((opt: any) => opt.shortName || opt.name || '');
        tagsControl.setValue(selectedTags, { emitEvent: true });
      }
      this.showValidationError = false;
    } else {
      this.control.setValue([], { emitEvent: true });
      this.selectedIds = [];

      const tagsControl = this.formGroup.get(fieldName + 'Tags');
      if (tagsControl) {
        tagsControl.setValue([], { emitEvent: true });
      }
      this.showValidationError = this.fields.isRequired;
    }

    const tagsControl = this.formGroup.get(fieldName + 'Tags');
    const tags = tagsControl ? tagsControl.value : [];
    let selectedQuestion = this.fields.displayName || '';
    if (selectedQuestion) {
      selectedQuestion = selectedQuestion.replace(/^\d+\.\s*/, '').replace(/[^a-zA-Z0-9)\]\s]+$/, '').trim();
    }

    this.handleTaskMappingDependency.emit({
      fieldName: fieldName,
      tags: tags,
      selectedQuestion: selectedQuestion
    });
  }

  /** Function to validate the card is selected or not then show error message */
  validateSelection(): boolean {
    const hasPrefilledValue = this.control?.value?.length > 0;
    const hasSelection = this.selectedIds && this.selectedIds.length > 0;
    if (this.fields.isRequired && !hasSelection && !hasPrefilledValue) {
      this.showValidationError = true;
      return false;
    }
    this.showValidationError = false;
    return true;
  }
}
